import { useMemo, useRef } from 'react';

import { EE_STATUS, STATUS, STATUS_REFRESH } from '@common/constants';
import { clamp, getBestPaymentUrl } from '@common/helpers';
import { EstimateStatus, GetEstimateDto } from '@common/models/save-estimate-dto';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { EstimateStatusParams, useFetchEstimateStatusQuery } from '@ee-api/estimate-api/estimateService';
import { useAppSelector } from '@redux/hooks';
import { selecteEeStatusList } from '@redux/slices/estimateSlice';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { skipToken } from '@reduxjs/toolkit/query';

import { AxiosBaseQueryFunction } from '../../api/axiosBaseQuery';

type useEstimateStatusReturn = TypedUseQueryHookResult<EstimateStatus, EstimateStatusParams, AxiosBaseQueryFunction> & {
    estimateStatus: EstimateStatus | undefined;
    isFetchingEstimateStatus: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function useEstimateStatus(): useEstimateStatusReturn {
    const { fetchedEstimate } = useFetchedEstimate();
    const statusList = useAppSelector(selecteEeStatusList);

    const statusPollingInterval = useRef(STATUS_REFRESH.OFF);
    const statusPollingCount = useRef(0);

    // this should not be required, but the useMemo is incrementing twice on each actual fetch
    const lastTimestamp = useRef(0);

    /**
     * Test if the fetched estimate needs to have its status checked or if we hae all the information we need.
     * Must exist, have an existing code, no paymentUrl, and status is OPEN or HOLD
     * @param estimate
     */
    const isEstimateStatusNeeded = (fetchedEstimate: GetEstimateDto | undefined): boolean => {
        return (
            !!fetchedEstimate &&
            !!fetchedEstimate.code &&
            !fetchedEstimate.paymentUrl?.length &&
            (fetchedEstimate.status === STATUS.OPEN || fetchedEstimate.status === STATUS.HOLD)
        );
    };

    const { estimateStatus, isFetchingEstimateStatus, ...rest } = useFetchEstimateStatusQuery(
        !!fetchedEstimate &&
            statusPollingCount.current < STATUS_REFRESH.MAX_ATTEMPTS &&
            isEstimateStatusNeeded(fetchedEstimate)
            ? {
                  params: {
                      code: fetchedEstimate.code,
                  },
              }
            : skipToken,
        {
            pollingInterval: statusPollingInterval.current * 1000,
            selectFromResult: (result) => {
                return {
                    estimateStatus: result.isError ? undefined : result.data,
                    isFetchingEstimateStatus: result.isFetching,
                    ...result,
                };
            },
        }
    );

    // increment the polling count whenever we fulfill a request
    statusPollingCount.current = useMemo(() => {
        if (!rest.fulfilledTimeStamp || lastTimestamp.current === rest.fulfilledTimeStamp) {
            return statusPollingCount.current ?? 0;
        }
        lastTimestamp.current = rest.fulfilledTimeStamp;
        return (statusPollingCount.current ?? 0) + 1;
    }, [rest.fulfilledTimeStamp]);

    statusPollingInterval.current = useMemo(() => {
        if (fetchedEstimate?.estimateNumber && isEstimateStatusNeeded(fetchedEstimate) && !estimateStatus) {
            return STATUS_REFRESH.MIN; // start polling for status
        }

        if (isFetchingEstimateStatus || !estimateStatus || !fetchedEstimate) {
            return statusPollingInterval.current ?? STATUS_REFRESH.OFF;
        }

        const isStatusFinal =
            getBestPaymentUrl(estimateStatus, fetchedEstimate) !== null ||
            statusList.some((status) => status === EE_STATUS.CLOSED);

        return isStatusFinal
            ? STATUS_REFRESH.OFF // we're done, stop polling
            : clamp(
                  STATUS_REFRESH.MIN * Math.pow(STATUS_REFRESH.BACK_OFF, statusPollingCount.current - 1),
                  STATUS_REFRESH.OFF,
                  STATUS_REFRESH.MAX
              );
    }, [estimateStatus, fetchedEstimate, isFetchingEstimateStatus, statusList]);

    return {
        estimateStatus,
        isFetchingEstimateStatus,
        ...rest,
    };
}

export default useEstimateStatus;
